package org.gymly.api.dto

import kotlinx.serialization.Serializable

@Serializable
data class ClientLoginRequestDto(val email: String, val password: String)

@Serializable
data class ClientLoginResponseDto(
    val accessToken: String,
    val tokenType: String,
    val expiresInSeconds: Long,
    val user: ClientLoginUserDto,
)

@Serializable
data class ClientLoginUserDto(
    val id: String,
    val displayName: String,
    val email: String? = null,
    val role: String,
    val preferredLocale: String? = null,
)

@Serializable
data class PortalMeDto(
    val id: String,
    val personId: String,
    val firstName: String,
    val lastName: String,
    val displayName: String? = null,
    val email: String? = null,
    val phone: String? = null,
    val photoUrl: String? = null,
    val preferredLocale: String? = null,
    val status: String,
    val statusLabel: String? = null,
    val branchName: String? = null,
    val mustChangePassword: Boolean = false,
)

@Serializable
data class PortalUpdateProfileDto(
    val firstName: String,
    val lastName: String,
    val email: String? = null,
    val phone: String? = null,
    val photoUrl: String? = null,
    val preferredLocale: String? = null,
)

@Serializable
data class PortalPaymentDto(
    val id: String,
    val orderId: String,
    val amountCents: Long,
    val currency: String,
    val method: String,
    val methodLabel: String? = null,
    val status: String,
    val statusLabel: String? = null,
    val notes: String? = null,
    val paidAt: String? = null,
    val paymentDate: String? = null,
    val canEdit: Boolean = false,
    val proofUrl: String? = null,
    val proofContentType: String? = null,
)

@Serializable
data class PortalBookingDto(
    val id: String,
    val scheduleId: String,
    val sessionDate: String,
    val weekday: Int,
    val startTime: String,
    val endTime: String,
    val title: String,
    val status: String,
    val statusLabel: String? = null,
    val canCancel: Boolean = false,
    val notes: String? = null,
)

@Serializable
data class PortalScheduleDto(
    val id: String,
    val weekday: Int,
    val startTime: String,
    val endTime: String,
    val title: String,
    val trainerName: String? = null,
    val shift: String? = null,
    val shiftLabel: String? = null,
    val capacity: Int = 0,
    val bookedCount: Long = 0,
    val availableSpots: Int = 0,
    val full: Boolean = false,
    val nextSessionDate: String? = null,
    val alreadyBooked: Boolean = false,
    val bookable: Boolean = true,
)

@Serializable
data class PortalMembershipOrderDto(
    val id: String,
    val orderNumber: Long = 0,
    val planName: String = "",
    val trainerName: String? = null,
    val orderDate: String = "",
    val amountCents: Long = 0,
    val currency: String = "VES",
    val status: String = "",
    val statusLabel: String = "",
    val endsAt: String? = null,
    val payments: List<String> = emptyList(),
    val paymentId: String? = null,
)

@Serializable
data class PortalPlanOptionDto(
    val id: String,
    val nombre: String,
    val priceCents: Long = 0,
    val priceUsdCents: Long = 0,
)

@Serializable
data class PortalTrainerOptionDto(
    val id: String,
    val nombre: String,
)

@Serializable
data class PortalMembershipOptionsDto(
    val planes: List<PortalPlanOptionDto> = emptyList(),
    val entrenadores: List<PortalTrainerOptionDto> = emptyList(),
)

/** Pago de membresía: el monto lo pone el plan según el método (Bs o $). */
@Serializable
data class PortalCreateMembershipPaymentDto(
    val planId: String,
    val trainerId: String,
    val method: String,
    val notes: String? = null,
    val paymentDate: String? = null,
)

@Serializable
data class PortalHomeDto(
    val profile: PortalMeDto,
    val recentPayments: List<PortalPaymentDto>,
    val upcomingClasses: List<PortalBookingDto>,
    val weekSchedules: List<PortalScheduleDto>,
    val schedulesAutomatic: Boolean = false,
    val today: String = "",
    val recentStoreOrders: List<PortalStoreOrderDto> = emptyList(),
)

@Serializable
data class PortalPasswordBodyDto(val currentPassword: String, val newPassword: String)

@Serializable
data class PortalPhotoBodyDto(val photoUrl: String?)

@Serializable
data class PortalCreateBookingDto(
    val scheduleId: String,
    val sessionDate: String? = null,
    val notes: String? = null,
)

@Serializable
data class PortalStoreProductDto(
    val id: String,
    val name: String,
    val category: String,
    val categoryLabel: String? = null,
    val priceCents: Long = 0,
    val priceUsdCents: Long = 0,
    val currency: String = "VES",
    val stockQty: Int = 0,
    val description: String? = null,
    val ingredients: String? = null,
    val videoUrl: String? = null,
    val imageUrl: String? = null,
)

@Serializable
data class PortalStoreCategoryDto(
    val id: String,
    val name: String,
)

@Serializable
data class PortalStoreProductsPageDto(
    val items: List<PortalStoreProductDto> = emptyList(),
    val page: Int = 0,
    val hasMore: Boolean = false,
    val categories: List<PortalStoreCategoryDto> = emptyList(),
    val totalCount: Long = 0,
)

@Serializable
data class PortalStoreOrderItemDto(
    val productId: String? = null,
    val productName: String = "",
    val qty: Int = 1,
    val unitPriceCents: Long = 0,
    val lineTotalCents: Long = 0,
)

@Serializable
data class PortalStoreOrderDto(
    val id: String,
    val saleNumber: Long = 0,
    val totalCents: Long = 0,
    val currency: String = "VES",
    val method: String = "CARD",
    val methodLabel: String? = null,
    val status: String = "PENDING",
    val statusLabel: String? = null,
    val saleDate: String = "",
    val itemsSummary: String? = null,
    val items: List<PortalStoreOrderItemDto> = emptyList(),
    val createdAt: String? = null,
    val proofUrl: String? = null,
    val proofContentType: String? = null,
)

@Serializable
data class PortalStoreCheckoutItemDto(
    val productId: String,
    val qty: Int,
)

@Serializable
data class PortalStoreCheckoutRequestDto(
    val method: String,
    val items: List<PortalStoreCheckoutItemDto>,
    val reference: String? = null,
    val paymentDate: String? = null,
)

/** Respuesta de GET /api/portal/payment-references/check: solo si la referencia ya existe ese día. */
@Serializable
data class PortalReferenceCheckDto(
    val duplicate: Boolean = false,
)

@Serializable
data class MarketingSlideDto(
    val id: String,
    val sortIndex: Int,
    val contentType: String? = null,
    val originalName: String? = null,
    val fullUrl: String,
    val thumbUrl: String,
)

@Serializable
data class MarketingPromotionDto(
    val id: String,
    val title: String,
    val details: String? = null,
    val displayType: String,
    val channel: String = "APP",
    val active: Boolean = true,
    val sortOrder: Int = 0,
    val startsAt: String? = null,
    val endsAt: String? = null,
    val scheduledAt: String? = null,
    val whatsappSendStatus: String? = null,
    val whatsappSentAt: String? = null,
    val whatsappError: String? = null,
    val createdAt: String? = null,
    val slides: List<MarketingSlideDto> = emptyList(),
    val videoUrl: String? = null,
    val pendingApproval: Boolean = false,
)

@Serializable
data class UpdateMarketingPromotionRequestDto(
    val title: String,
    val details: String? = null,
    val displayType: String = "SINGLE",
    val channel: String = "APP",
    val active: Boolean = true,
    val sortOrder: Int = 0,
    val startsAt: String? = null,
    val endsAt: String? = null,
    val scheduledAt: String? = null,
    val videoUrl: String? = null,
)

@Serializable
data class PortalClientNotificationDto(
    val id: String,
    val category: String,
    val categoryLabel: String? = null,
    val title: String,
    val body: String? = null,
    val read: Boolean = false,
    val createdAt: String? = null,
    val relativeLabel: String? = null,
)

@Serializable
data class PortalUnreadCountDto(
    val unreadCount: Long = 0,
)

@Serializable
data class PortalNotificationPreferencesDto(
    val paymentReminders: Boolean = true,
    val classReminders: Boolean = true,
    val promotions: Boolean = false,
    val staffMessages: Boolean = true,
)
